import React, { useCallback, useMemo, useState } from 'react';
import { useFocusEffect } from '@react-navigation/native';

import { useIsLoggedIn } from '@order/AuthMachine';
import { LoadingAnimation, SweetpassSignedOut } from '@order/components';
import type { SubscriptionCreditCard } from '@order/graphql';
import {
  TierName,
  useGetPaymentSubscriptionProfileQuery,
} from '@order/graphql';
import { useSweetpassTier } from '@order/hooks';

import { TierUpgradeCelebrationModal } from '../../components/TierUpgradeCelebrationModal';
import { useNavigateToMainTabs } from '../../navigation';
import type { SweetpassCheckoutScreenProps } from '../../navigation/AppNavigation.props';
import {
  useSubscribeToSweetpassPlus,
  useSweetpassCheckoutContentfulData,
} from './hooks';
import { SweetpassCheckoutScreenContent } from './SweetpassCheckoutScreenContent';

// ────────────────────────────────────────────────────────────────────────────────

export const SweetpassCheckoutScreen = (
  props: SweetpassCheckoutScreenProps,
) => {
  const { navigation, route } = props;
  const { planId, redirectToAfterUpgrade, redirectToOrderId } =
    route.params ?? {};

  // ─── Context ─────────────────────────────────────────────────────────

  const navigateToMainTabs = useNavigateToMainTabs();

  // ─── State ───────────────────────────────────────────────────────────

  const [hasCustomerBeenUpgraded, setHasCustomerBeenUpgraded] = useState(false);
  const [selectedCreditCard, selectCreditCard] =
    useState<Partial<SubscriptionCreditCard>>();

  // ─── Queries ─────────────────────────────────────────────────────────

  const { isSweetpassEnabled, isSweetpassTierLoading, sweetpassTierName } =
    useSweetpassTier('cache-and-network');

  const { content: loadedContent, isLoading: isContentLoading } =
    useSweetpassCheckoutContentfulData();

  const [paymentSubscriptionProfileResponse] =
    useGetPaymentSubscriptionProfileQuery({
      requestPolicy: 'cache-and-network',
    });

  // ─── Derived Data ────────────────────────────────────────────────────

  const shouldRedirectToOrderStatusAfterUpgrade =
    redirectToAfterUpgrade === 'order-status' &&
    redirectToOrderId !== undefined;

  const paymentSubscriptionProfile =
    paymentSubscriptionProfileResponse.data?.paymentSubscriptionProfile;

  const isPaymentSubscriptionProfileQueryResolved =
    paymentSubscriptionProfileResponse !== undefined;

  const creditCards: ReadonlyArray<Partial<SubscriptionCreditCard>> =
    paymentSubscriptionProfile?.creditCards ?? [];

  const isLoading =
    isSweetpassTierLoading ||
    isContentLoading ||
    !isPaymentSubscriptionProfileQueryResolved;

  const hasContent = isSweetpassEnabled && Boolean(loadedContent);

  const isSweetpassEnabledForCustomer =
    !isSweetpassTierLoading && isSweetpassEnabled;
  const isSweetpassPlusMember = sweetpassTierName === TierName.SweetpassPlus;

  // ─── Callbacks ───────────────────────────────────────────────────────

  const navigateAfterUpgrade = useCallback(() => {
    if (shouldRedirectToOrderStatusAfterUpgrade) {
      // NOTE: We use `replace` here because we don't want to keep the current screen
      //       in the navigation history since it should be unavailable after the upgrade.
      navigation.replace('OrderStatus', { orderId: redirectToOrderId });

      return;
    }

    void navigateToMainTabs('LoyaltyTab', {
      screen: 'SweetpassHome',
    });
  }, [
    navigation,
    redirectToOrderId,
    shouldRedirectToOrderStatusAfterUpgrade,
    navigateToMainTabs,
  ]);

  const navigateToHome = useCallback(() => {
    void navigateToMainTabs('HomeTab', {
      screen: 'Home',
    });
  }, [navigateToMainTabs]);

  // ─── Subscription Hook ───────────────────────────────────────────────

  const handleCta = useSubscribeToSweetpassPlus({
    planId,
    paymentId: selectedCreditCard?.id,
    redirectToAfterUpgrade,
    setHasCustomerBeenUpgraded,
  });

  // ─── Redirects ───────────────────────────────────────────────────────

  // NOTE: Redirect conditions are presented as an array,
  //       making it easy to check several conditions.

  const redirectToSweetpassHomeScreenConditions =
    useMemo<ScreenRedirectConditions>(
      () => [
        !hasCustomerBeenUpgraded &&
          isSweetpassEnabledForCustomer &&
          isSweetpassPlusMember,
      ],
      [
        hasCustomerBeenUpgraded,
        isSweetpassEnabledForCustomer,
        isSweetpassPlusMember,
      ],
    );
  const redirectToSweetpassUpgradeScreenConditions =
    useMemo<ScreenRedirectConditions>(
      () => [isSweetpassEnabledForCustomer && !planId],
      [isSweetpassEnabledForCustomer, planId],
    );
  const redirectToHomeScreenConditions = useMemo<ScreenRedirectConditions>(
    () => [!isSweetpassEnabledForCustomer, !isLoading && !hasContent],
    [isSweetpassEnabledForCustomer, isLoading, hasContent],
  );

  /**
   * Redirects customer to the Sweetpass Home screen, if any of the following conditions are met.
   *
   * - The current customer has Sweetpass+ subscription (already upgraded)
   */
  useFocusEffect(
    useCallback(() => {
      const shouldRedirect =
        redirectToSweetpassHomeScreenConditions.some(Boolean);

      if (!shouldRedirect) return;

      void navigateToMainTabs('LoyaltyTab', { screen: 'SweetpassHome' });
    }, [navigateToMainTabs, redirectToSweetpassHomeScreenConditions]),
  );

  /**
   * Redirects customer to the Home Screen, if any of the following conditions are met.
   *
   * - The current customer does not have Sweetpass enabled.
   */
  useFocusEffect(
    useCallback(() => {
      const shouldRedirect = redirectToHomeScreenConditions.some(Boolean);

      if (!shouldRedirect) return;

      void navigateToMainTabs('HomeTab', { screen: 'Home' });
    }, [navigateToMainTabs, redirectToHomeScreenConditions]),
  );

  /**
   * Redirects customer to the Sweetpass Upgrade screen, if the appropriate plan ID is not available.
   */
  useFocusEffect(
    useCallback(() => {
      const shouldRedirect =
        redirectToSweetpassUpgradeScreenConditions.some(Boolean);

      if (!shouldRedirect) return;

      navigation.replace('SweetpassUpgrade');
    }, [navigation, redirectToSweetpassUpgradeScreenConditions]),
  );

  /**
   * Redirects logged-out customers to the login, then to Sweetpass Upgrade screen.
   */
  const isLoggedIn = useIsLoggedIn();
  const handlePressSignIn = useCallback(() => {
    navigation.replace('Auth', {
      screen: 'Login',
      params: { redirect: 'sweetpassUpgrade' },
    });
  }, [navigation]);

  if (!isLoggedIn) {
    return (
      <SweetpassSignedOut
        testID="sweetpass-signed-out"
        onPressSignIn={handlePressSignIn}
      />
    );
  }

  // ─────────────────────────────────────────────────────────────────────

  if (isLoading) {
    return <LoadingAnimation />;
  }

  if (!isSweetpassEnabled || !loadedContent || !paymentSubscriptionProfile) {
    return null;
  }

  return (
    <>
      <SweetpassCheckoutScreenContent
        testID="sweetpass-checkout-screen"
        termsAndConditions={loadedContent.termsAndConditions}
        selectedCreditCard={selectedCreditCard}
        creditCards={creditCards}
        handleSelectCreditCard={selectCreditCard}
        ctaHandler={handleCta}
      />

      <TierUpgradeCelebrationModal
        testID="sweetpass-checkout-celebration-modal"
        onDismiss={navigateToHome}
        onExplore={navigateAfterUpgrade}
        visible={hasCustomerBeenUpgraded}
      />
    </>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ScreenRedirectConditions = readonly boolean[];
